import React from 'react';
import { v4 as uuid } from 'uuid';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { ProductSlotsTemplate } from '../../../../templates/ProductSlotsTemplate';
import { fetchSlotsTeleorientation } from '../../../../modules/slots';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { daysInMonth } from '../../../../utils/dates';
import { DateSlots } from '../../../../templates/ProductSlotsTemplate/components/ProductSlots';
import { toast } from 'react-toastify';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { format, isSameDay } from 'date-fns';
import { HeadProduct } from '../../../../components/HeadProduct';

import { ReactComponent as Stethoscope } from '../../../../assets/stethoscope.svg';
import { useModuleContext } from '../../../../contexts/ModuleContext';
import { usePatientContext } from '../../../../contexts/PatientContext';
import { isOlderThan } from '../../../../utils/validators';
import { appointmentForSameDayRule } from '../../../../utils/catalogRules';
import useOnCloseSchedule from '../../../../hooks/useOnCloseSchedule';
import { usePatientExternal } from '../../../../store/patientExternal';

export function ProductSlotsPage() {
  const { navigate, goBack } = useExecutiveCheckupRouter();
  const { selectedService: service } = useServiceContext();
  const { changeSchedule, selectedItem: schedule } = useScheduleContext();
  const { patientSelected } = usePatientContext();
  const { patient: externalPatient } = usePatientExternal();
  const { logged } = useModuleContext();

  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);

  const [slots, setSlots] = React.useState<DateSlots[]>([]);

  const { onCloseSchedule } = useOnCloseSchedule({});

  const isAppointmentOnSameDay = React.useMemo(() => {
    const [product] = service.products;
    return (
      product.rules?.find(rule => {
        return appointmentForSameDayRule(rule?.rule_id || '');
      })?.value === 'true'
    );
  }, [service]);

  const checkPediatricPatient = () => {
    if (patientSelected) return !isOlderThan(patientSelected.birthDate, 16);
    if (externalPatient?.name) return !isOlderThan(externalPatient?.birthDate, 16);
    return false;
  };

  const loadSlotsDates = async (date: Date): Promise<DateSlots[]> => {
    try {
      const startDate = new Date();

      if (date.getMonth() > startDate.getMonth() || date.getFullYear() > startDate.getFullYear()) {
        startDate.setDate(1);
        startDate.setMonth(date.getMonth());
        startDate.setFullYear(date.getFullYear());
      }

      let endDate = new Date();
      endDate.setDate(startDate.getDate() + 1);

      if (!isAppointmentOnSameDay) {
        const daysOnMonth = daysInMonth(date.getMonth() + 1, date.getFullYear());
        endDate = new Date(date.getFullYear(), date.getMonth(), daysOnMonth);
      }

      const specialtyId = checkPediatricPatient() ? 2 : 1;

      const result = await fetchSlotsTeleorientation({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        specialtyId
      });

      const { data, error } = result;
      if (error) toast.error('Não foi possível consultar agenda no momento.');

      if (!error) {
        if (!data.length) toast.error('Sem agenda disponível no momento.');

        if (data.length) {
          setSlots(data.map(item => ({ id: uuid(), date: item.date, slot: item.slots } as DateSlots)));

          return data.map(item => {
            const dateSplitted = item.date.split(/[ T]/);
            return { id: uuid(), date: dateSplitted[0] } as DateSlots;
          });
        }
      }
      return [];
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível consultar slots.');
    }
    return [];
  };

  const loadSlotsHours = async (date: Date): Promise<DateSlots[]> => {
    const selectedDateSlots = slots.find(item => isSameDay(new Date(item.date), date));

    return selectedDateSlots?.slot.map((item: any) => ({ id: uuid(), date: item.hour, slot: item } as DateSlots));
  };

  const onGoBack = () => {
    if (logged) return onCloseSchedule();
    return goBack();
  };

  const submit = async (slotSelected: DateSlots) => {
    const scheduleFactory = ScheduleItemPropsBuilder();
    scheduleFactory.setTime({
      slots: [slotSelected.slot],
      time: new Date(slotSelected.date),
      ...slotSelected
    });

    changeSchedule({ ...schedule, ...scheduleFactory.setSlotsOptions([slotSelected.slot]) });

    navigate(Page.APPOINTMENT_CONFIRMATION);
  };

  return (
    <>
      <ProductSlotsTemplate
        headTitle="Escolha uma data e horário"
        goBack={onGoBack}
        productName={service.products?.[0]?.name ?? ''}
        productId={service.products?.[0]?.id ?? ''}
        handleSlot={submit}
        loadSlotsDates={loadSlotsDates}
        loadSlotsHours={loadSlotsHours}
        handleSelectSlot={_ => {}}
        serviceName={checkPediatricPatient() ? 'Pediatra' : 'Clínico Geral'}
        showDates={!isAppointmentOnSameDay}
        customSlotsHead={
          <HeadProduct
            name={checkPediatricPatient() ? 'Pediatra' : 'Clínico Geral'}
            description="Consulta virtual"
            productIcon={<Stethoscope />}
          />
        }
      />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />
    </>
  );
}
